import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronUp, ExternalLink, RefreshCw, Mail, Save, Eye, Undo2 } from "lucide-react";
import { API, axios, formatIcelandicNumber } from "@/utils/constants";
import { UtistandandiSkuldDetails } from "@/pages/UtistandandiSkuldDetails";
import { NaestiManudurDetails } from "@/pages/NaestiManudurDetails";
import { OgreidirReikningarDetails } from "@/pages/OgreidirReikningarDetails";

const buildEmailBody = (row, extraPerMonth, newMonthly, hasInnborganir) => {
  const innborgunNote = hasInnborganir
    ? `\nTekið hefur verið tillit til innborgana sem þegar hafa verið inntar af hendi.\n`
    : '';
  return `Kæri/Kæra ${row.borrower_name},

Við höfum uppfært greiðsluáætlun láns þíns hjá FONDO ehf. vegna leiðréttingar á greiðsluupphæðum.

Lánsfjárhæð: ${formatIcelandicNumber(row.loan_amount)} kr.
Lántökugjald: ${formatIcelandicNumber(row.lantokugjald)} kr.
Heildarfjárhæð láns: ${formatIcelandicNumber(row.loan_amount + row.lantokugjald)} kr.
${innborgunNote}
Vegna þess að fyrri greiðslur (${row.paid_months_count} mán.) voru reiknaðar á lægri upphæð, hefur mismunur að fjárhæð ${formatIcelandicNumber(Math.abs(row.difference))} kr. verið dreift jafnt á eftirstandandi ${row.paid_months_count} greiðslur.

Hækkun á greiðslu: +${formatIcelandicNumber(extraPerMonth)} kr. á mánuð
Ný mánaðarleg greiðsla frá 01.04.2026: ${formatIcelandicNumber(newMonthly)} kr.

Þessar breytingar taka gildi frá 01.04.2026. Greiðslur sem þegar hafa verið greiddar breytast ekki.

Ef þú hefur einhverjar spurningar, vinsamlegast hafðu samband við okkur í síma eða netfangi.

Kveðja,
FONDO ehf.
Krokháls 7, 110 Reykjavík
info@fondo.is`;
};

export const Skyrslu = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = searchParams.get("section") || "agent";
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [redistributing, setRedistributing] = useState({});

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRow, setDialogRow] = useState(null);
  const [dialogResult, setDialogResult] = useState(null); // { total_mismunur, extra_per_month, future_months_count }
  const [emailBody, setEmailBody] = useState("");
  const [sending, setSending] = useState(false);
  const [undoing, setUndoing] = useState({});

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/payment-discrepancy`);
      setReport(res.data);
    } catch (e) {
      toast.error("Villa við að sækja skýrslu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const handleUndoRedistribution = async (row) => {
    if (!window.confirm(`Afturkalla Endurreikna fyrir ${row.borrower_name}?\n\nGreiðsluáætlun fer til baka í upprunalega upphæð ÁN lántökugjalds.`)) return;
    setUndoing(prev => ({ ...prev, [row.loan_id]: true }));
    try {
      const res = await axios.post(`${API}/loans/${row.loan_id}/undo-redistribution`);
      toast.success(res.data.message || "Endurreikna afturkallað");
      await fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Villa við afturköllun");
    } finally {
      setUndoing(prev => ({ ...prev, [row.loan_id]: false }));
    }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // Step 1: Open dialog (runs redistribute, shows result + email preview)
  const openRedistributeDialog = async (row) => {
    setRedistributing(prev => ({ ...prev, [row.loan_id]: true }));
    try {
      const res = await axios.post(`${API}/loans/${row.loan_id}/redistribute-discrepancy`, {
        start_date: "01.04.2026"
      });
      if (res.data.success) {
        const newMonthly = row.monthly_correct + res.data.extra_per_month;
        setDialogRow(row);
        setDialogResult(res.data);
        setEmailBody(buildEmailBody(row, res.data.extra_per_month, newMonthly, res.data.has_innborganir));
        setDialogOpen(true);
        await fetchReport(); // refresh data
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Villa við endurútreikning");
    } finally {
      setRedistributing(prev => ({ ...prev, [row.loan_id]: false }));
    }
  };

  // Step 2a: Save only (close dialog)
  const handleSaveOnly = () => {
    toast.success("Greiðsluáætlun uppfærð. Enginn tölvupóstur sendur.");
    setDialogOpen(false);
  };

  // Step 2b: Save and send email
  const handleSaveAndSend = async () => {
    if (!dialogRow) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/email/send-to-loan/${dialogRow.loan_id}`,
        null,
        {
          params: {
            email_type: "notification",
            subject_text: "Uppfærsla á greiðsluáætlun láns þíns hjá FONDO ehf.",
            message: emailBody
          }
        }
      );
      toast.success("Greiðsluáætlun uppfærð og tölvupóstur sendur!");
      setDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Villa við sendingu tölvupósts");
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-gray-500">Hleð skýrslu...</div>
    </div>
  );
  if (!report) return null;

  const { summary, report: rows } = report;

  // Sort: virkt first, eldra lán (no loan_number) grouped, closed last
  const sortRow = (r) => {
    if (r.status === 'closed') return 3;
    if (!r.loan_number) return 2; // eldra lán
    return 1; // virkt/approved
  };
  const sortedRows = [...rows].sort((a, b) => sortRow(a) - sortRow(b) || b.difference - a.difference);

  const problemLoans = sortedRows.filter(r => Math.abs(r.difference) > 500);
  const okLoans = sortedRows.filter(r => Math.abs(r.difference) <= 500);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4">
        {section === "agent" && (
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Agent</h1>
        )}

        {section === "utistandandi" && <UtistandandiSkuldDetails embedded />}

        {section === "nastu12" && <NaestiManudurDetails embedded />}

        {section === "uppgjor" && <OgreidirReikningarDetails embedded />}

        {section === "agent" && (
        <>
        <Tabs defaultValue="mismunur">
          <TabsList className="mb-4">
            <TabsTrigger value="mismunur" data-testid="tab-mismunur">Greiðslumismunur</TabsTrigger>
            <TabsTrigger value="yfirlit" data-testid="tab-yfirlit">Yfirlit</TabsTrigger>
          </TabsList>

          <TabsContent value="mismunur">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Card><CardContent className="p-4">
                <p className="text-xs text-gray-500">Lán með greiðslur</p>
                <p className="text-2xl font-bold text-gray-800">{summary.total_loans_with_paid}</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-xs text-gray-500">Innheimt (raunverulegt)</p>
                <p className="text-xl font-bold text-gray-800">{formatIcelandicNumber(summary.total_actual_charged)} kr.</p>
              </CardContent></Card>
              <Card className="border-blue-200 bg-blue-50"><CardContent className="p-4">
                <p className="text-xs text-blue-600">Rétt upphæð ({summary.current_rate_used}%)</p>
                <p className="text-xl font-bold text-blue-800">{formatIcelandicNumber(summary.total_correct_charged)} kr.</p>
              </CardContent></Card>
              <Card className={`border-2 ${summary.total_difference > 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
                <CardContent className="p-4">
                  <p className={`text-xs ${summary.total_difference > 0 ? 'text-red-600' : 'text-green-600'}`}>Mismunur (vantar)</p>
                  <p className={`text-xl font-bold ${summary.total_difference > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {summary.total_difference > 0 ? '+' : ''}{formatIcelandicNumber(summary.total_difference)} kr.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>Athugasemd:</strong> Mismunurinn miðast við núverandi vexti ({summary.current_rate_used}%).
                Smelltu á <strong>Endurreikna</strong> til að dreifa mismuninum jafnt á eftirstandandi greiðslur frá 01.04.2026.
                Liðnar greiðslur (sem þegar hafa verið sendar) <strong>breytast aldrei</strong>.
              </p>
            </div>

            {/* Problem Loans */}
            {problemLoans.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" /> Lán með mismun ({problemLoans.length})
                </h2>
                <div className="space-y-2">
                  {(() => {
                    let lastGroup = null;
                    return problemLoans.map(row => {
                      const group = row.status === 'closed' ? 'closed' : !row.loan_number ? 'eldra' : 'virkt';
                      const showLabel = group !== lastGroup;
                      lastGroup = group;
                      return (
                        <React.Fragment key={row.loan_id}>
                          {showLabel && group === 'eldra' && (
                            <div className="flex items-center gap-2 mt-3 mb-1">
                              <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Eldra lán</span>
                              <div className="flex-1 border-t border-gray-200" />
                            </div>
                          )}
                          {showLabel && group === 'closed' && (
                            <div className="flex items-center gap-2 mt-3 mb-1">
                              <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Lokið</span>
                              <div className="flex-1 border-t border-gray-200" />
                            </div>
                          )}
                      {/* Header row */}
                      <div className={`border rounded-lg bg-white overflow-hidden ${row.status === 'closed' ? 'border-gray-200 opacity-75' : 'border-red-200'}`}>
                        <div className="flex items-center gap-2 p-3">
                        <button onClick={() => toggleExpand(row.loan_id)} className="shrink-0 p-0.5">
                          {expanded[row.loan_id]
                            ? <ChevronUp className="h-4 w-4 text-gray-400" />
                            : <ChevronDown className="h-4 w-4 text-gray-400" />}
                        </button>

                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleExpand(row.loan_id)}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">{row.borrower_name}</span>
                            {row.loan_number && <Badge variant="outline" className="text-[10px] px-1 py-0">Lán {row.loan_number}</Badge>}
                            <Badge className={`text-[10px] px-1 py-0 ${row.status === 'disbursed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                              {row.status === 'disbursed' ? 'Virkt' : row.status === 'approved' ? 'Samþykkt' : row.status}
                            </Badge>
                            {row.issues.includes('rangir_vextir') && (
                              <Badge className="text-[10px] px-1 py-0 bg-orange-100 text-orange-800">Rangir vextir ({row.stored_rate}%)</Badge>
                            )}
                            {row.issues.includes('lantokugjald_vantar') && (
                              <Badge className="text-[10px] px-1 py-0 bg-purple-100 text-purple-800">Lántökugjald vantar</Badge>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 flex gap-3 flex-wrap">
                            <span>Lánsfjárhæð: {formatIcelandicNumber(row.loan_amount)} kr.</span>
                            <span>Lántökugjald: {formatIcelandicNumber(row.lantokugjald)} kr.</span>
                            <span>{row.paid_months_count} mán. greiddir</span>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <div className="text-[10px] text-gray-500">Innheimt</div>
                          <div className="text-sm font-semibold">{formatIcelandicNumber(row.actual_charged_total)} kr.</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] text-blue-600">Rétt upphæð</div>
                          <div className="text-sm font-semibold text-blue-700">{formatIcelandicNumber(row.correct_charged_total)} kr.</div>
                        </div>
                        <div className="text-right shrink-0 min-w-[72px]">
                          <div className="text-[10px] text-red-600">Mismunur</div>
                          <div className={`text-sm font-bold ${row.difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {row.difference > 0 ? '+' : ''}{formatIcelandicNumber(row.difference)} kr.
                          </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`view-loan-btn-${row.loan_id}`}
                            onClick={() => navigate(`/greidslur/${row.loan_id}`)}
                            className="h-7 text-xs px-2 border-blue-300 text-blue-600 hover:bg-blue-50 gap-1"
                            title="Skoða greiðsluáætlun"
                          ><Eye className="h-3 w-3" />Skoða</Button>
                          {!row.redistribution_done ? (
                            <>
                              <Button
                                size="sm"
                                data-testid={`redistribute-btn-${row.loan_id}`}
                                disabled={redistributing[row.loan_id]}
                                onClick={() => openRedistributeDialog(row)}
                                className="h-7 text-xs px-2 bg-orange-600 hover:bg-orange-700 gap-1"
                                title="Dreifa mismun jafnt á eftirstandandi greiðslur frá 01.04.2026"
                              >
                                {redistributing[row.loan_id]
                                  ? <RefreshCw className="h-3 w-3 animate-spin" />
                                  : <><RefreshCw className="h-3 w-3" />Endurreikna</>}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`dismiss-btn-${row.loan_id}`}
                                className="h-7 text-xs px-2 border-gray-300 text-gray-500 hover:bg-gray-50"
                                title="Hunsa þennan mismun"
                                onClick={async () => {
                                  try {
                                    await axios.patch(`${API}/loans/${row.loan_id}`, { redistribution_done: true });
                                    await fetchReport();
                                  } catch (e) { toast.error("Villa"); }
                                }}
                              >Hunsa</Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`undo-btn-${row.loan_id}`}
                              disabled={undoing[row.loan_id]}
                              onClick={() => handleUndoRedistribution(row)}
                              className="h-7 text-xs px-2 border-amber-400 text-amber-700 hover:bg-amber-50 gap-1"
                              title="Afturkalla Endurreikna — fara til baka í upprunalega áætlun"
                            >
                              {undoing[row.loan_id]
                                ? <RefreshCw className="h-3 w-3 animate-spin" />
                                : <><Undo2 className="h-3 w-3" />Afturkalla</>}
                            </Button>
                          )}
                          <button onClick={() => navigate(`/greidslur/${row.loan_id}`)} className="p-1 hover:bg-gray-100 rounded" title="Opna greiðsluáætlun">
                            <ExternalLink className="h-3.5 w-3.5 text-blue-500" />
                          </button>
                        </div>
                      </div>

                      {/* Expanded detail */}
                      {expanded[row.loan_id] && (
                        <div className="border-t border-red-100 bg-red-50 p-3">
                          <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
                            <div>
                              <p className="text-gray-500">Mánaðarleg greiðsla (innheimt)</p>
                              <p className="font-semibold">{formatIcelandicNumber(row.monthly_actual)} kr./mán.</p>
                            </div>
                            <div>
                              <p className="text-blue-600">Rétt greiðsla ({summary.current_rate_used}%)</p>
                              <p className="font-semibold text-blue-700">{formatIcelandicNumber(row.monthly_correct)} kr./mán.</p>
                            </div>
                            <div>
                              <p className="text-red-600">Mismunur á mánuð</p>
                              <p className={`font-semibold ${row.monthly_correct - row.monthly_actual > 0 ? 'text-red-700' : 'text-green-700'}`}>
                                {row.monthly_correct - row.monthly_actual > 0 ? '+' : ''}{formatIcelandicNumber(Math.round(row.monthly_correct - row.monthly_actual))} kr.
                              </p>
                            </div>
                          </div>
                          {row.actual_per_month.length > 0 && (
                            <table className="w-full text-xs border-collapse">
                              <thead>
                                <tr className="bg-white">
                                  <th className="border border-red-200 px-2 py-1 text-left">Mán.</th>
                                  <th className="border border-red-200 px-2 py-1 text-left">Gjalddagi</th>
                                  <th className="border border-red-200 px-2 py-1 text-right">Innheimt</th>
                                  <th className="border border-red-200 px-2 py-1 text-right">Rétt upphæð</th>
                                  <th className="border border-red-200 px-2 py-1 text-right">Mismunur</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.actual_per_month.map(m => {
                                  const diff = row.monthly_correct - m.charged;
                                  return (
                                    <tr key={m.month} className="bg-white">
                                      <td className="border border-red-200 px-2 py-0.5">{m.month}</td>
                                      <td className="border border-red-200 px-2 py-0.5">{m.date}</td>
                                      <td className="border border-red-200 px-2 py-0.5 text-right">{formatIcelandicNumber(m.charged)} kr.</td>
                                      <td className="border border-red-200 px-2 py-0.5 text-right text-blue-700">{formatIcelandicNumber(row.monthly_correct)} kr.</td>
                                      <td className={`border border-red-200 px-2 py-0.5 text-right font-semibold ${diff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {diff > 0 ? '+' : ''}{formatIcelandicNumber(Math.round(diff))} kr.
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                      </div>
                        </React.Fragment>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* OK Loans */}
            {okLoans.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-green-700 mb-2">Lán án verulegra frávika ({okLoans.length})</h2>
                <div className="border border-green-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-green-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Lántaki</th>
                        <th className="px-3 py-2 text-center">Lán nr.</th>
                        <th className="px-3 py-2 text-center">Mánuðir</th>
                        <th className="px-3 py-2 text-right">Innheimt</th>
                        <th className="px-3 py-2 text-right">Mismunur</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {okLoans.map(row => (
                        <tr key={row.loan_id} className="border-t border-green-100 hover:bg-green-50">
                          <td className="px-3 py-1.5">
                            <button onClick={() => navigate(`/greidslur/${row.loan_id}`)} className="text-blue-600 hover:underline">{row.borrower_name}</button>
                          </td>
                          <td className="px-3 py-1.5 text-center text-gray-500">{row.loan_number || '-'}</td>
                          <td className="px-3 py-1.5 text-center">{row.paid_months_count}</td>
                          <td className="px-3 py-1.5 text-right">{formatIcelandicNumber(row.actual_charged_total)} kr.</td>
                          <td className={`px-3 py-1.5 text-right font-semibold ${Math.abs(row.difference) < 100 ? 'text-green-600' : 'text-gray-600'}`}>
                            {row.difference >= 0 ? '+' : ''}{formatIcelandicNumber(row.difference)} kr.
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <button onClick={() => navigate(`/greidslur/${row.loan_id}`)} className="p-1 hover:bg-gray-100 rounded">
                              <ExternalLink className="h-3 w-3 text-blue-400" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="yfirlit">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-sm">Innheimtur</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {rows.slice(0, 20).map(row => (
                      <div key={row.loan_id} className="flex justify-between items-center text-xs py-1 border-b border-gray-100">
                        <button onClick={() => navigate(`/greidslur/${row.loan_id}`)} className="text-blue-600 hover:underline truncate max-w-[200px]">
                          {row.borrower_name}
                        </button>
                        <div className="text-right ml-2">
                          <span className="font-semibold">{formatIcelandicNumber(row.actual_charged_total)} kr.</span>
                          <span className="text-gray-400 ml-1">({row.paid_months_count} mán.)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-sm">Samantekt</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Fjöldi lána með greiðslur</span>
                      <span className="font-bold">{summary.total_loans_with_paid}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Samtals innheimt</span>
                      <span className="font-bold">{formatIcelandicNumber(summary.total_actual_charged)} kr.</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-blue-600">Rétt upphæð ({summary.current_rate_used}%)</span>
                      <span className="font-bold text-blue-700">{formatIcelandicNumber(summary.total_correct_charged)} kr.</span>
                    </div>
                    <div className={`flex justify-between py-2 rounded px-2 ${summary.total_difference > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                      <span className={`font-semibold ${summary.total_difference > 0 ? 'text-red-700' : 'text-green-700'}`}>Heildarsmismunur</span>
                      <span className={`font-bold text-lg ${summary.total_difference > 0 ? 'text-red-700' : 'text-green-700'}`}>
                        {summary.total_difference >= 0 ? '+' : ''}{formatIcelandicNumber(summary.total_difference)} kr.
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 pt-1">
                      Lán með frávik: {problemLoans.length} af {rows.length}<br/>
                      Núverandi vextir: {summary.current_rate_used}%
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        </>
        )}
      </div>

      {/* Redistribution confirmation dialog with email preview */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-orange-500" />
              Endurreikna greiðsluáætlun — {dialogRow?.borrower_name}
            </DialogTitle>
          </DialogHeader>

          {dialogRow && dialogResult && (
            <div className="space-y-4">
              {/* Result summary */}
              <div className="grid grid-cols-3 gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm">
                <div>
                  <p className="text-xs text-gray-500">Heildar mismunur</p>
                  <p className="font-bold text-red-700">+{formatIcelandicNumber(dialogResult.total_mismunur)} kr.</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bætt við á mánuð</p>
                  <p className="font-bold text-orange-700">+{formatIcelandicNumber(dialogResult.extra_per_month)} kr.</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Eftirstandandi mánuðir</p>
                  <p className="font-bold text-gray-800">{dialogResult.future_months_count} mán. (frá 01.04.2026)</p>
                </div>
              </div>

              <div className="p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                Liðnar greiðslur ({dialogRow.paid_months_count} mán.) breytast <strong>ekki</strong>. Breytingarnar taka gildi frá 01.04.2026.
              </div>

              {dialogResult.has_innborganir && (
                <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                  Innborganir fundust á þessum láni. Greiðsluáætlun er reiknuð út frá <strong>raunverulegri eftirstöðvar</strong> lánsins — innborganirnar eru teknar með í reikninginn.
                </div>
              )}

              {/* Email preview */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-semibold">Tilkynning til viðskiptavinar</span>
                  <span className="text-xs text-gray-400">(breytanlegt)</span>
                </div>
                <Textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={14}
                  className="text-xs font-mono resize-none"
                  data-testid="redistribution-email-body"
                />
              </div>

              {/* Action buttons */}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)} disabled={sending}>
                  Hætta við
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveOnly}
                  disabled={sending}
                  className="gap-1"
                  data-testid="save-only-btn"
                >
                  <Save className="h-3.5 w-3.5" />
                  Geyma
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveAndSend}
                  disabled={sending}
                  className="bg-blue-600 hover:bg-blue-700 gap-1"
                  data-testid="save-and-send-btn"
                >
                  {sending
                    ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    : <><Mail className="h-3.5 w-3.5" />Geyma og senda</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Skyrslu;
